'use client';

import { useInfiniteQuery } from '@tanstack/react-query';
import cx from 'classix';
import { useAtomValue } from 'jotai';
import { useHydrateAtoms } from 'jotai/utils';
import {
  useEffect,
  useMemo,
  useRef,
} from 'react';

import APP_CONFIG from '@/configs/app-config';
import BlogCard from '@/features/blog/components/blog-card/blog-card';
import { getCookieCurrency } from '@/features/currency/cookies/cookie-currency';
import { isMapViewAtom } from '@/features/listing/atoms/is-map-view-atom';
import { listingsViewAtom } from '@/features/listing/atoms/listings-view-atom';
import ListingCard from '@/features/listing/components/listing-card/listing-card';
import MapViewSwitch from '@/features/listing/components/listings-filters/map-view-switch/map-view-switch';
import ListingsPagination from '@/features/listing/components/listings-section/listings-pagination/listings-pagination';
import {
  LISTINGS_DEFAULT_SEARCH_PARAMS,
  LISTINGS_PER_PAGE,
} from '@/features/listing/constants/listings-filters-constants';
import getListings from '@/features/listing/requests/get-listings';
import {
  ExtraFieldsNames,
  PaginationNames,
} from '@/features/listing/types/listing-filters-types';
import {
  type ListingCardView,
  type ListingsCollection,
  type ListingWithUser,
} from '@/features/listing/types/listing-types';
import { getUserBearerCookie } from '@/features/user/cookies/cookie-user-bearer';
import Loader from '@/ui/loader/loader';

import styles from './listings-list.module.scss';

interface ListingsListProps {
  listingsCollection: ListingsCollection,
  savedListingView?: ListingCardView | null,
  showBlogPost?: boolean,
  showPagination?: boolean,
  isInfiniteScrollEnabled: boolean,
}

interface BlogPostTrigger {
  type: 'blogPost',
}

export default function ListingsList(props: ListingsListProps) {
  const {
    isInfiniteScrollEnabled,
    listingsCollection,
    savedListingView,
    showBlogPost,
    showPagination,
  } = props;

  useHydrateAtoms([[listingsViewAtom, savedListingView ?? APP_CONFIG.defaultListingCardView]]);

  const isMapView = useAtomValue(isMapViewAtom);
  const listingView = useAtomValue(listingsViewAtom);

  const bearerTokenCookie = getUserBearerCookie();

  const triggerRef = useRef<HTMLDivElement | null>(null);
  const loadMoreObserverRef = useRef<IntersectionObserver | null>(null);

  const blogPostPlaceOnPage = 8;
  const listingsPerPage = Number(LISTINGS_DEFAULT_SEARCH_PARAMS[PaginationNames.PageSize]);

  const isInfinityScrollActive = isInfiniteScrollEnabled && !!bearerTokenCookie;

  const query = useInfiniteQuery({
    enabled: isInfinityScrollActive,
    getNextPageParam: (lastPage, pages) => (lastPage.length > 0 ? pages.length + 1 : undefined),
    initialData: {
      pageParams: [1],
      pages: [listingsCollection.items],
    },
    initialPageParam: 1,
    queryFn: async ({ pageParam }: { pageParam: number }) => {
      const currentCurrencyNameCookie = getCookieCurrency();

      const requestSearchParams = new URLSearchParams();
      requestSearchParams.set('search_params.bookmark', 'Bookmarked');
      // we increase listing per page by one, because bookmarks doesn't have blog post
      requestSearchParams.set(PaginationNames.PageSize, String(LISTINGS_PER_PAGE + 1));
      requestSearchParams.set(PaginationNames.PageNumber, String(pageParam));
      requestSearchParams.set(ExtraFieldsNames.IncludeTotalCount, 'true');

      const { items } = await getListings(
        requestSearchParams.toString(),
        currentCurrencyNameCookie,
        bearerTokenCookie,
      );

      return items;
    },
    queryKey: ['bookmarks'],
    staleTime: 0,
  });

  const infinityListings = useMemo(() => query.data.pages.flat(), [query.data]);

  const cardsData: (ListingWithUser | BlogPostTrigger)[] = isInfinityScrollActive
    ? infinityListings : [...listingsCollection.items];

  if (showBlogPost && !isInfinityScrollActive) {
    cardsData.splice(blogPostPlaceOnPage, 0, { type: 'blogPost' });
  }

  useEffect(() => {
    if (!isInfiniteScrollEnabled) return;

    const target = triggerRef.current;
    if (!target) {
      return;
    }

    // Observer callback
    const observerCallback = (entries: IntersectionObserverEntry[]) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && !query.isFetching && query.hasNextPage) {
          // eslint-disable-next-line @typescript-eslint/no-floating-promises
          query.fetchNextPage();
        }
      });
    };

    if (!loadMoreObserverRef.current) {
      loadMoreObserverRef.current = new IntersectionObserver(observerCallback, {
        root: null,
        rootMargin: '200px',
        threshold: 1, // Fully in view
      });
    }

    loadMoreObserverRef.current.observe(target);

    // eslint-disable-next-line consistent-return
    return () => {
      if (loadMoreObserverRef.current) loadMoreObserverRef.current.disconnect();
      loadMoreObserverRef.current = null;
    };
  }, [isInfiniteScrollEnabled, query]);

  return (
    <>
      <div>
        <div
          className={cx(
            styles.listingsList,
            styles[`listingsList__${listingView}`],
            isMapView && styles.listingsList__isMapView,
          )}
        >
          {cardsData.map((card) => {
            if ('type' in card) {
              return (
                <div key="blog-post" className={styles.listingsList_listingCard}>
                  <BlogCard />
                </div>
              );
            }

            return (
              <div key={card.id} className={styles.listingsList_listingCard}>
                <ListingCard
                  listing={card}
                  listingViewVariant={listingView}
                  messageSrc="search-grid"
                />
              </div>
            );
          })}
        </div>

        <div className={styles.listingsList_mapViewSwitch}>
          <MapViewSwitch />
        </div>

        {isInfinityScrollActive && (
          <>
            <div
              ref={triggerRef}
              className={styles.listingsList_loadMoreTrigger}
            />

            {query.isFetching && (
              <div className={styles.listingsList_loader}>
                <Loader size={12} />
              </div>
            )}
          </>
        )}
      </div>

      {(!isInfinityScrollActive && showPagination && listingsCollection.count > listingsPerPage) && (
        <ListingsPagination
          listingsCount={listingsCollection.count}
          listingsPerPage={listingsPerPage}
        />
      )}
    </>
  );
}
